import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Conference } from '@core/models/conference.model';
import { Itinerary } from '@core/models/itinerary.model';
import { UserSessionRequest } from '@core/models/user-session.request.model';
import { PaginatedApiResponse } from '@core/models/common/paginated-api-response.model';
import { ApiResponse } from '@core/models/common/api-response.model';
import { ApiResponseHandler } from '@core/handlers/api-response.handler';
import { NextItem } from '@core/models/common/next-item.model';
import { QuestionSet } from '@core/models/question-set.model';
import { APP_CONFIG } from '@core/config/app-config.token';
import { AppConfig } from '@core/config/app-config.model';

@Injectable({
  providedIn: 'root',
})
export class ConferenceService {
  private readonly conferenceUrl: string;
  private readonly featuredConferenceUrl: string;
  private readonly upcomingConferenceUrl: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly cfg: AppConfig
  ) {
    this.conferenceUrl = `${this.cfg.serviceApiUrl}/conferences`;
    this.featuredConferenceUrl = `${this.cfg.serviceApiUrl}/conferences/featured`;
    this.upcomingConferenceUrl = `${this.cfg.serviceApiUrl}/conferences/upcoming`;
  }

  getConferences(): Observable<Conference[]> {
    return this.http.get<PaginatedApiResponse<Conference[]>>(this.conferenceUrl).pipe(
      map((response) => ApiResponseHandler.extractData<Conference[]>(response) as Conference[]),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getConferencesWithParams(
    params: Record<string, string>
  ): Observable<PaginatedApiResponse<Conference>> {
    return this.http
      .get<PaginatedApiResponse<Conference>>(this.conferenceUrl, { params })
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  getFeaturedConferences(): Observable<Conference[]> {
    return this.http.get<PaginatedApiResponse<Conference[]>>(this.featuredConferenceUrl).pipe(
      map((response) => ApiResponseHandler.extractData<Conference[]>(response) as Conference[]),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getUpcomingConferences(): Observable<Conference[]> {
    return this.http.get<PaginatedApiResponse<Conference[]>>(this.upcomingConferenceUrl).pipe(
      map((response) => ApiResponseHandler.extractData<Conference[]>(response) as Conference[]),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getConference(uuid: string): Observable<Conference> {
    return this.http.get<ApiResponse<Conference>>(`${this.conferenceUrl}/${uuid}`).pipe(
      map((response) => ApiResponseHandler.extractData<Conference>(response) as Conference),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getItineraries(conferenceUuid: string): Observable<Itinerary[]> {
    return this.http
      .get<ApiResponse<Itinerary[]>>(`${this.conferenceUrl}/${conferenceUuid}/itineraries`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<Itinerary[]>(response) as Itinerary[]),
        catchError(ApiResponseHandler.handleError)
      );
  }

  storeUserSession(userSession: UserSessionRequest): Observable<ApiResponse<NextItem>> {
    return this.http
      .post<ApiResponse<NextItem>>(`${this.conferenceUrl}/itineraries`, userSession)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  getQuestionSet(liveUuid: string, questionSetUuid: string): Observable<QuestionSet> {
    return this.http
      .get<
        ApiResponse<QuestionSet>
      >(`${this.conferenceUrl}/${liveUuid}/questionSets/${questionSetUuid}`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<QuestionSet>(response) as QuestionSet),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getUserSessionIds(conferenceUuid: string): Observable<number[]> {
    return this.http
      .get<ApiResponse<number[]>>(`${this.conferenceUrl}/${conferenceUuid}/user/sessions`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<number[]>(response) as number[]),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getSessionQuestionSet(
    conferenceUuid: string,
    sessionUuid: string,
    sessionId: number
  ): Observable<QuestionSet> {
    return this.http
      .get<
        ApiResponse<QuestionSet>
      >(`${this.conferenceUrl}/${conferenceUuid}/sessions/${sessionUuid}/${sessionId}`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<QuestionSet>(response) as QuestionSet),
        catchError(ApiResponseHandler.handleError)
      );
  }
}
